import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle } from 'lucide-react';
import { companyInfo } from '../data/mock';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    country: '',
    product: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Get backend URL from environment
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      // Submit to backend API
      const response = await fetch(`${backendUrl}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        console.log('Inquiry submitted successfully:', data);
        setIsSubmitted(true);
        
        // Reset form after 3 seconds
        setTimeout(() => {
          setFormData({
            name: '',
            email: '',
            company: '',
            country: '',
            product: '',
            message: ''
          });
          setIsSubmitted(false);
        }, 3000);
      } else {
        alert('Failed to submit inquiry. Please try again or contact us directly.');
      }
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      alert('Failed to submit inquiry. Please try again or contact us directly via email or WhatsApp.');
    }
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-b from-amber-50/30 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-orange-500 font-semibold text-sm uppercase tracking-wider">Get In Touch</span>
          <h2 className="text-4xl md:text-5xl font-bold text-amber-950 mt-3 mb-4">
            Request a Quote
          </h2>
          <p className="text-gray-600 text-lg max-w-3xl mx-auto">
            Ready to start importing premium agricultural products? Fill out the form below and our team will get back to you within 24 hours.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-12">
          {/* Contact Information */}
          <div className="lg:col-span-1 space-y-8">
            <div>
              <h3 className="text-2xl font-bold text-amber-950 mb-6">Contact Information</h3>
              <p className="text-gray-600 mb-8">
                We're here to answer any questions you may have about our products and services.
              </p>
            </div>

            {/* Contact Details */}
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Mail className="text-green-700" size={24} />
                </div>
                <div>
                  <h4 className="font-semibold text-amber-950 mb-1">Email</h4>
                  <a href={`mailto:${companyInfo.email}`} className="text-gray-600 hover:text-green-700 transition-colors block">
                    {companyInfo.email}
                  </a>
                  <a href={`mailto:${companyInfo.emailSecondary}`} className="text-gray-600 hover:text-green-700 transition-colors block mt-1">
                    {companyInfo.emailSecondary}
                  </a>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Phone className="text-orange-500" size={24} />
                </div>
                <div>
                  <h4 className="font-semibold text-amber-950 mb-1">Phone / WhatsApp</h4>
                  <a href={`tel:${companyInfo.phone}`} className="text-gray-600 hover:text-green-700 transition-colors block">
                    {companyInfo.phone}
                  </a>
                  <a 
                    href={`https://wa.me/919326255288`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-green-600 hover:text-green-700 text-sm font-medium transition-colors inline-block mt-1"
                  >
                    Chat on WhatsApp →
                  </a>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <MapPin className="text-green-700" size={24} />
                </div>
                <div>
                  <h4 className="font-semibold text-amber-950 mb-1">Location</h4>
                  <p className="text-gray-600">{companyInfo.address}</p>
                </div>
              </div>
            </div>

            {/* Additional Info Box */}
            <div className="bg-gradient-to-br from-green-700 to-green-800 rounded-2xl p-6 text-white mt-8">
              <h4 className="font-bold text-lg mb-2">Quick Response</h4>
              <p className="text-green-50 text-sm">
                Our export team typically responds to inquiries within 24 hours during business days.
              </p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
              {isSubmitted ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="text-green-700" size={40} />
                  </div>
                  <h3 className="text-2xl font-bold text-amber-950 mb-3">Thank You!</h3>
                  <p className="text-gray-600 text-lg">
                    Your inquiry has been received. Our team will contact you shortly.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-semibold text-amber-950 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-green-500 focus:ring-2 focus:ring-green-200 outline-none transition-all"
                        placeholder="John Doe"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-semibold text-amber-950 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-green-500 focus:ring-2 focus:ring-green-200 outline-none transition-all"
                        placeholder="john@company.com"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="company" className="block text-sm font-semibold text-amber-950 mb-2">
                        Company Name *
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-green-500 focus:ring-2 focus:ring-green-200 outline-none transition-all"
                        placeholder="ABC Trading Co."
                      />
                    </div>

                    <div>
                      <label htmlFor="country" className="block text-sm font-semibold text-amber-950 mb-2">
                        Country *
                      </label>
                      <input
                        type="text"
                        id="country"
                        name="country"
                        value={formData.country}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-green-500 focus:ring-2 focus:ring-green-200 outline-none transition-all"
                        placeholder="United States"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="product" className="block text-sm font-semibold text-amber-950 mb-2">
                      Product Interest *
                    </label>
                    <select
                      id="product"
                      name="product"
                      value={formData.product}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-green-500 focus:ring-2 focus:ring-green-200 outline-none transition-all"
                    >
                      <option value="">Select a product category</option>
                      <option value="Fresh Fruits">Fresh Fruits</option>
                      <option value="Spices">Spices</option>
                      <option value="Turmeric (Organic) — Fresh, Finger, Flakes & Powder">Turmeric (Organic) — Fresh, Finger, Flakes & Powder</option>
                      <option value="Ginger (Organic) — Fresh, Flakes & Powder">Ginger (Organic) — Fresh, Flakes & Powder</option>
                      <option value="Buckwheat (Organic) — Unhulled & Flour">Buckwheat (Organic) — Unhulled & Flour</option>
                      <option value="Large Black Cardamom (Organic)">Large Black Cardamom (Organic)</option>
                      <option value="Coffee Beans Robusta & Arabica (Organic)">Coffee Beans Robusta & Arabica (Organic)</option>
                      <option value="Cocoa — Fresh & Dried Beans (Organic)">Cocoa — Fresh & Dried Beans (Organic)</option>
                      <option value="Nutmeg (Organic) — Dried & Shelled">Nutmeg (Organic) — Dried & Shelled</option>
                      <option value="Cardamom (Green) (Organic)">Cardamom (Green) (Organic)</option>
                      <option value="Black Pepper (Organic)">Black Pepper (Organic)</option>
                      <option value="Other / Multiple Products">Other / Multiple Products</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-amber-950 mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows="5"
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-green-500 focus:ring-2 focus:ring-green-200 outline-none transition-all resize-none"
                      placeholder="Please provide details about your requirements, quantities, and any specific questions..."
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center space-x-2"
                  >
                    <span>Submit Inquiry</span>
                    <Send size={20} />
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
